import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Cookies from "universal-cookie";
import {
  Box, Typography, IconButton, Tooltip, Collapse, Slide, Popper, Grow,
  CircularProgress, ToggleButtonGroup, ToggleButton,
  TextField, MenuItem, Switch, Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ReplayIcon from "@mui/icons-material/Replay";
import FilterListIcon from "@mui/icons-material/FilterList";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import DoDisturbOnIcon from "@mui/icons-material/DoDisturbOn";
import SaveIcon from "@mui/icons-material/Save";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BlockIcon from "@mui/icons-material/Block";
import { selectRoles } from "../../reduxStore/RolesReducer";
import arrivalValidityRuleAPI from "../../Network/ArrivalValidityRule";
import geofenceAPI from "../../Network/Geofence";
import DateFieldYMD from "../../Components/DateFieldYMD";

const cookies = new Cookies();

/* ---- design tokens (from UI_UX_AGENT_STANDARDS §1, MUI variant §F.2) ---- */
const t = {
  surface: "#FFFFFF",
  surfaceMuted: "#F1F5F9",
  control: "#FFFFFF",
  border: "#CBD5E1",
  borderSoft: "#E2E8F0",
  borderStrong: "#94A3B8",
  text: "#0F172A",
  textMuted: "#64748B",
  primary: "#1C64F2",
  primarySoft: "rgba(28, 100, 242, 0.10)",
  primaryDark: "#1A56DB",
  onPrimary: "#FFFFFF",
  success: "#10B981",
  successSoft: "rgba(16, 185, 129, 0.12)",
  warning: "#F59E0B",
  warningSoft: "rgba(245, 158, 11, 0.12)",
  danger: "#EF4444",
  dangerSoft: "rgba(239, 68, 68, 0.10)",
};

const PAGE_SIZE = 20;

/* native input helpers (no x-date-pickers) */
const toTimeInput = (s) => (s ? s.substring(0, 5) : "");
const fromTimeInput = (v) => (v && /^\d{2}:\d{2}$/.test(v) ? v + ":00" : (v || ""));

const authParams = () => ({
  username: cookies.get("user_name") || null,
  user_id: cookies.get("user_id") || null,
  account_name: cookies.get("account_name") || null,
  account_id: cookies.get("account_id") || null,
  sid: cookies.get("sid") || null,
  baseurl: cookies.get("baseUrl") || null,
  id: cookies.get("id") || null,
});

const getMsg = (result, fallback) => {
  const m = result?.data?.data?.message;
  return (typeof m === "string" ? m : m?.text) || fallback;
};

const emptyForm = {
  id: null,
  name: "",
  transaction_scope: "external", // module is external-only (خارجي)
  destination_geofence_id: "",
  effective_from: "",
  effective_to: "",
  is_closed: 0,
  allowed_from: "",
  allowed_to: "",
  invalid_note: "",
  enabled: 1,
};

export default function ArrivalValidityRules() {
  const [t18n] = useTranslation("common");
  const navigate = useNavigate();
  const rolesRedux = useSelector(selectRoles);
  const canCreate = rolesRedux?.add_master?.value;
  const canUpdate = rolesRedux?.update_master?.value;
  const canDelete = rolesRedux?.delete_master?.value;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [destinations, setDestinations] = useState([]);

  const [showFilters, setShowFilters] = useState(false);
  const [pending, setPending] = useState({ name: "", enabled: "" });
  const [applied, setApplied] = useState({ name: "", enabled: "" });

  const [form, setForm] = useState(null);        // null = modal closed
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [confirmRow, setConfirmRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showRecompute, setShowRecompute] = useState(false);

  const [menu, setMenu] = useState({ anchor: null, row: null });
  const closeTimer = useRef(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const activeFilterCount = useMemo(
    () => Object.values(applied).filter((v) => v !== "" && v !== null && v !== undefined).length,
    [applied]
  );

  /* -------- data -------- */
  useEffect(() => { loadDestinations(); }, []);
  useEffect(() => { loadData(); }, [page, applied]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDestinations = async () => {
    const res = await geofenceAPI.getGeofenceList({ params: { ...authParams(), port_type_ids: [2] } });
    if (res?.status && res?.data?.data) {
      const list = Array.isArray(res.data.data) ? res.data.data : res.data.data?.data;
      setDestinations(list || []);
    }
  };

  const loadData = async () => {
    if (!cookies.get("active_session") || cookies.get("active_session") !== "yes") {
      navigate("/redirect");
      return;
    }
    setLoading(true);
    const res = await arrivalValidityRuleAPI.arrivalValidityRules({
      params: { page, page_size: PAGE_SIZE, ...applied },
    });
    setLoading(false);
    if (res?.status && res?.data?.status && res?.data?.data?.data) {
      setRows(res.data.data.data);
      setTotal(res.data.data.total_records || 0);
    } else {
      setRows([]); setTotal(0);
      if (res?.data && !res?.data?.status) toast.error(getMsg(res, t18n("NETWORK_MESSAGES.unknownError")));
    }
  };

  const applyFilters = () => { setApplied({ ...pending }); setPage(1); };
  const clearFilters = () => {
    const empty = { name: "", enabled: "" };
    setPending(empty); setApplied(empty); setPage(1);
  };

  /* -------- row menu (Popper, hover w/ grace) -------- */
  const openMenu = (e, row) => { if (closeTimer.current) clearTimeout(closeTimer.current); setMenu({ anchor: e.currentTarget, row }); };
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setMenu({ anchor: null, row: null }), 140); };
  const cancelClose = () => { if (closeTimer.current) clearTimeout(closeTimer.current); };
  const closeMenu = () => setMenu({ anchor: null, row: null });

  /* -------- create / edit -------- */
  const openCreate = () => { setErrors({}); setForm({ ...emptyForm }); };
  const openEdit = (row) => {
    closeMenu();
    setErrors({});
    setForm({
      id: row.id,
      name: row.name || "",
      transaction_scope: row.transaction_scope || "external",
      destination_geofence_id: row.destination_geofence_id || "",
      effective_from: (row.effective_from || "").substring(0, 10),
      effective_to: (row.effective_to || "").substring(0, 10),
      is_closed: row.is_closed != null ? row.is_closed : 0,
      allowed_from: toTimeInput(row.allowed_from),
      allowed_to: toTimeInput(row.allowed_to),
      invalid_note: row.invalid_note || "",
      enabled: row.enabled != null ? row.enabled : 1,
    });
  };

  const submit = async () => {
    const errs = {};
    const closed = form.is_closed === 1;
    if (!closed && (!form.allowed_from || !/^\d{2}:\d{2}$/.test(form.allowed_from))) errs.allowed_from = t18n("GLOBAL.mandatory_field");
    if (!closed && (!form.allowed_to || !/^\d{2}:\d{2}$/.test(form.allowed_to))) errs.allowed_to = t18n("GLOBAL.mandatory_field");
    if (form.effective_from && form.effective_to && form.effective_from > form.effective_to)
      errs.effective_to = t18n("ARRIVAL_VALIDITY_RULES.to_before_from");
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      name: form.name?.trim() || "",
      transaction_scope: form.transaction_scope,
      destination_geofence_id: form.destination_geofence_id || "",
      effective_from: form.effective_from ? `${form.effective_from} 00:00:00` : "",
      effective_to: form.effective_to ? `${form.effective_to} 23:59:59` : "",
      is_closed: closed ? 1 : 0,
      allowed_from: closed ? "" : fromTimeInput(form.allowed_from),
      allowed_to: closed ? "" : fromTimeInput(form.allowed_to),
      invalid_note: form.invalid_note?.trim() || "",
      enabled: form.enabled === 1 ? 1 : 0,
    };

    setSaving(true);
    let res;
    if (form.id) res = await arrivalValidityRuleAPI.editArrivalValidityRule({ id: form.id, data: payload });
    else res = await arrivalValidityRuleAPI.addArrivalValidityRule({ data: payload });
    setSaving(false);

    if (res?.status && res?.data?.status) {
      toast.success(t18n("NETWORK_MESSAGES.infoAddedSuccessfully"));
      setForm(null);
      loadData();
    } else {
      toast.error(getMsg(res, t18n("NETWORK_MESSAGES.unknownError")));
    }
  };

  const doDelete = async () => {
    if (!confirmRow) return;
    setDeleting(true);
    const res = await arrivalValidityRuleAPI.deleteArrivalValidityRule({ data: { id: confirmRow.id } });
    setDeleting(false);
    if (res?.status && res?.data?.status) {
      toast.success(t18n("NETWORK_MESSAGES.infoDeletedSuccessfully"));
      setConfirmRow(null);
      loadData();
    } else {
      toast.error(getMsg(res, t18n("NETWORK_MESSAGES.unknownError")));
    }
  };

  /* -------- label helpers -------- */
  const destLabel = (id) => {
    if (!id) return t18n("ARRIVAL_VALIDITY_RULES.any_destination");
    const d = destinations.find((x) => x.id === id);
    return d ? d.name : `#${id}`;
  };
  const effRange = (row) => {
    const f = row.effective_from ? row.effective_from.substring(0, 10) : null;
    const to = row.effective_to ? row.effective_to.substring(0, 10) : null;
    if (f && to) return `${f} → ${to}`;
    if (f) return `${f} → …`;
    if (to) return `… → ${to}`;
    return t18n("ARRIVAL_VALIDITY_RULES.all_dates");
  };

  /* -------- shared sx -------- */
  const cardSx = { bgcolor: t.surface, border: `1px solid ${t.border}`, borderRadius: 3 };
  const roundBtn = (bg, color, hoverBg) => ({
    width: 42, height: 42, borderRadius: "50%", bgcolor: bg, color,
    transition: "all .2s", "&:hover": { bgcolor: hoverBg, transform: "scale(1.06)" },
  });

  const pill = (bg, color, Icon, label) => (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.25, py: 0.4,
      borderRadius: 999, fontSize: 12, fontWeight: 700, fontFamily: "Cairo-Medium",
      bgcolor: bg, color,
    }}>
      {Icon && <Icon sx={{ fontSize: 15 }} />}{label}
    </Box>
  );

  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 3 }, fontFamily: "Cairo" }}>

      {/* ---------------- Page header ---------------- */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 46, height: 46, borderRadius: 2.5, bgcolor: t.primarySoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FactCheckIcon sx={{ color: t.primary, fontSize: 26 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: t.text, fontFamily: "Cairo-Bold", lineHeight: 1.2 }}>
              {t18n("ARRIVAL_VALIDITY_RULES.title")}
            </Typography>
            <Typography sx={{ fontSize: 13, color: t.textMuted, fontFamily: "Cairo" }}>
              {t18n("ARRIVAL_VALIDITY_RULES.subtitle")}
            </Typography>
          </Box>
        </Box>

        {/* action buttons (RTL: rendered visual-left) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title={<span style={{ fontFamily: "Cairo" }}>{t18n("ARRIVAL_VALIDITY_RULES.filters")}</span>}>
            <Box sx={{ position: "relative" }}>
              <IconButton
                onClick={() => setShowFilters((s) => !s)}
                sx={roundBtn(showFilters || activeFilterCount ? t.primary : t.primarySoft, showFilters || activeFilterCount ? t.onPrimary : t.primary, showFilters || activeFilterCount ? t.primaryDark : t.primarySoft)}
              >
                <FilterListIcon />
              </IconButton>
              {activeFilterCount > 0 && (
                <Box sx={{
                  position: "absolute", top: -2, left: -2, minWidth: 18, height: 18, px: 0.5,
                  borderRadius: 999, bgcolor: t.warning, color: "#fff", fontSize: 11, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{activeFilterCount}</Box>
              )}
            </Box>
          </Tooltip>

          {canUpdate && (
            <Tooltip title={<span style={{ fontFamily: "Cairo" }}>{t18n("ARRIVAL_VALIDITY_RULES.recompute")}</span>}>
              <IconButton onClick={() => setShowRecompute(true)} sx={roundBtn(t.warningSoft, t.warning, "rgba(245,158,11,0.2)")}>
                <ReplayIcon />
              </IconButton>
            </Tooltip>
          )}

          {canCreate && (
            <Tooltip title={<span style={{ fontFamily: "Cairo" }}>{t18n("ARRIVAL_VALIDITY_RULES.add")}</span>}>
              <IconButton onClick={openCreate} sx={roundBtn(t.successSoft, t.success, "rgba(16,185,129,0.2)")}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ---------------- Filter panel ---------------- */}
      <Collapse in={showFilters} timeout={220} unmountOnExit>
        <Box sx={{ ...cardSx, p: 2.5, mb: 2.5 }}>
          <Box component="form" onSubmit={(e) => { e.preventDefault(); applyFilters(); }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 2 }}>
              <FieldLabel label={t18n("ARRIVAL_VALIDITY_RULES.name")}>
                <TextField
                  value={pending.name}
                  onChange={(e) => setPending({ ...pending, name: e.target.value })}
                  placeholder={t18n("ARRIVAL_VALIDITY_RULES.name")}
                  size="small" fullWidth sx={filterFieldSx}
                />
              </FieldLabel>
            </Box>

            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 2 }}>
              <Box>
                <FilterCaption>{t18n("ARRIVAL_VALIDITY_RULES.enabled")}</FilterCaption>
                <ToggleButtonGroup
                  exclusive size="small"
                  value={pending.enabled === "" ? "" : String(pending.enabled)}
                  onChange={(e, v) => setPending({ ...pending, enabled: v ?? "" })}
                  sx={toggleGroupSx}
                >
                  <ToggleButton value="">{t18n("ARRIVAL_VALIDITY_RULES.all")}</ToggleButton>
                  <ToggleButton value="1">{t18n("ARRIVAL_VALIDITY_RULES.enabled")}</ToggleButton>
                  <ToggleButton value="0">{t18n("ARRIVAL_VALIDITY_RULES.disabled")}</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, mt: 2.5 }}>
              <Button type="submit" variant="contained" disableElevation
                sx={{ bgcolor: t.primary, "&:hover": { bgcolor: t.primaryDark }, fontFamily: "Cairo-Bold", borderRadius: 2, px: 3 }}>
                {t18n("GLOBAL.search")}
              </Button>
              <Button onClick={clearFilters} variant="outlined"
                sx={{ color: t.textMuted, borderColor: t.border, "&:hover": { borderColor: t.borderStrong, bgcolor: t.surfaceMuted }, fontFamily: "Cairo-Bold", borderRadius: 2, px: 3 }}>
                {t18n("GLOBAL.clear")}
              </Button>
            </Box>
            <button type="submit" style={{ display: "none" }} aria-hidden />
          </Box>
        </Box>
      </Collapse>

      {/* ---------------- Timeline list ---------------- */}
      {loading ? (
        <Box sx={{ ...cardSx, p: 6, textAlign: "center" }}>
          <CircularProgress size={28} sx={{ color: t.primary }} />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ ...cardSx, p: 6, textAlign: "center" }}>
          <FactCheckIcon sx={{ fontSize: 44, color: t.borderStrong, mb: 1 }} />
          <Typography sx={{ fontFamily: "Cairo-Medium", color: t.textMuted }}>{t18n("ARRIVAL_VALIDITY_RULES.no_records")}</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {rows.map((row) => (
            <Box key={row.id} sx={{ ...cardSx, p: { xs: 1.75, md: 2.5 }, opacity: row.enabled ? 1 : 0.62 }}>
              {/* header: name + type + destination + effective range + actions */}
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontFamily: "Cairo-Bold", fontSize: 16, color: t.text }}>
                    {row.name || t18n("ARRIVAL_VALIDITY_RULES.untitled")}
                  </Typography>
                  {pill(t.surfaceMuted, t.text, null, destLabel(row.destination_geofence_id))}
                  {!row.enabled ? pill(t.surfaceMuted, t.textMuted, DoDisturbOnIcon, t18n("ARRIVAL_VALIDITY_RULES.disabled")) : null}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontFamily: "Cairo-Medium", fontSize: 13, color: t.textMuted }}>
                    <Box component="span" dir="ltr" sx={{ unicodeBidi: "isolate" }}>{effRange(row)}</Box>
                  </Typography>
                  {(canUpdate || canDelete) ? (
                    <IconButton size="small"
                      onMouseEnter={(e) => openMenu(e, row)} onMouseLeave={scheduleClose} onClick={(e) => openMenu(e, row)}
                      sx={{ color: t.textMuted, "& .MuiSvgIcon-root": { transition: "transform .22s" }, "&:hover": { color: t.primary, bgcolor: t.primarySoft, "& .MuiSvgIcon-root": { transform: "rotate(90deg)" } } }}>
                      <MoreHorizIcon />
                    </IconButton>
                  ) : null}
                </Box>
              </Box>

              {/* 24h timeline */}
              <TimelineBar rule={row} tt={t18n} />
            </Box>
          ))}
        </Box>
      )}

      {/* pagination */}
      <Box sx={{ ...cardSx, display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.25, mt: 1.5, bgcolor: t.surfaceMuted, flexWrap: "wrap", gap: 1 }}>
        <Typography sx={{ fontSize: 13, color: t.textMuted, fontFamily: "Cairo" }}>
          {t18n("ARRIVAL_VALIDITY_RULES.total_records")}: {total}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
            sx={{ color: t.textMuted, "&:hover": { bgcolor: t.surface } }}><ChevronRightIcon /></IconButton>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(page - 2, totalPages - 4));
            return start + i;
          }).filter((n) => n >= 1 && n <= totalPages).map((n) => (
            <Box key={n} onClick={() => setPage(n)}
              sx={{
                width: 32, height: 32, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "Cairo-Medium",
                bgcolor: page === n ? t.primary : "transparent", color: page === n ? t.onPrimary : t.textMuted,
                "&:hover": { bgcolor: page === n ? t.primary : t.surface },
              }}>{n}</Box>
          ))}
          <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            sx={{ color: t.textMuted, "&:hover": { bgcolor: t.surface } }}><ChevronLeftIcon /></IconButton>
        </Box>
      </Box>

      {/* ---------------- Row action Popper ---------------- */}
      <Popper open={Boolean(menu.anchor)} anchorEl={menu.anchor} placement="bottom-end" transition sx={{ zIndex: 1400 }}>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "top left" }}>
            <Box
              onMouseEnter={cancelClose} onMouseLeave={scheduleClose}
              sx={{ mt: 0.5, width: "fit-content", minWidth: 150, bgcolor: t.surface, border: `1px solid ${t.border}`, borderRadius: 2, boxShadow: "0 10px 30px rgba(0,0,0,0.15)", overflow: "hidden", py: 0.5 }}
            >
              {canUpdate && (
                <Box onClick={() => openEdit(menu.row)} sx={menuItemSx(t)}>
                  <EditIcon sx={{ fontSize: 18, color: t.success }} />
                  <span style={{ fontFamily: "Cairo-Medium" }}>{t18n("GLOBAL.action_edit")}</span>
                </Box>
              )}
              {canDelete && (
                <Box onClick={() => { setConfirmRow(menu.row); closeMenu(); }} sx={{ ...menuItemSx(t), color: t.danger, "&:hover": { bgcolor: t.dangerSoft } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 18, color: t.danger }} />
                  <span style={{ fontFamily: "Cairo-Medium" }}>{t18n("GLOBAL.action_delete")}</span>
                </Box>
              )}
            </Box>
          </Grow>
        )}
      </Popper>

      {/* ---------------- Create / Edit modal ---------------- */}
      {form && (
        <ModalShell onClose={() => !saving && setForm(null)} busy={saving} title={form.id ? t18n("ARRIVAL_VALIDITY_RULES.update_title") : t18n("ARRIVAL_VALIDITY_RULES.add_title")} icon={FactCheckIcon} maxWidth={620}>
          <Box sx={{ p: 3, overflowY: "auto", maxHeight: "62vh" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <Box sx={{ gridColumn: { xs: "auto", sm: "1 / span 2" } }}>
                <FieldLabel label={t18n("ARRIVAL_VALIDITY_RULES.name")}>
                  <TextField value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} size="small" fullWidth sx={fieldSx} />
                </FieldLabel>
              </Box>

              <Box sx={{ gridColumn: { xs: "auto", sm: "1 / span 2" } }}>
                <FieldLabel label={t18n("ARRIVAL_VALIDITY_RULES.destination")}>
                  <TextField select value={form.destination_geofence_id || ""} onChange={(e) => setForm({ ...form, destination_geofence_id: e.target.value })} size="small" fullWidth sx={fieldSx} SelectProps={selectProps}>
                    <MenuItem value="">{t18n("ARRIVAL_VALIDITY_RULES.any_destination")}</MenuItem>
                    {destinations.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                  </TextField>
                </FieldLabel>
              </Box>

              <FieldLabel label={t18n("ARRIVAL_VALIDITY_RULES.effective_from")}>
                <DateFieldYMD value={form.effective_from} onChange={(v) => setForm({ ...form, effective_from: v })} sx={fieldSx} iconColor={t.textMuted} />
              </FieldLabel>

              <FieldLabel label={t18n("ARRIVAL_VALIDITY_RULES.effective_to")} error={errors.effective_to}>
                <DateFieldYMD value={form.effective_to} onChange={(v) => setForm({ ...form, effective_to: v })} sx={fieldSx} iconColor={t.textMuted} />
              </FieldLabel>

              {/* closed toggle spans both columns */}
              <Box sx={{ gridColumn: { xs: "auto", sm: "1 / span 2" }, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: form.is_closed === 1 ? t.dangerSoft : t.surfaceMuted, borderRadius: 2, px: 2, py: 1 }}>
                <Typography sx={{ fontFamily: "Cairo-Medium", fontSize: 14, color: form.is_closed === 1 ? t.danger : t.text }}>
                  {t18n("ARRIVAL_VALIDITY_RULES.is_closed")}
                </Typography>
                <Switch checked={form.is_closed === 1} onChange={(e) => setForm({ ...form, is_closed: e.target.checked ? 1 : 0 })} />
              </Box>

              <FieldLabel label={t18n("ARRIVAL_VALIDITY_RULES.allowed_from")} required={form.is_closed !== 1} error={errors.allowed_from}>
                <TextField type="time" value={form.allowed_from} disabled={form.is_closed === 1}
                  onChange={(e) => setForm({ ...form, allowed_from: e.target.value })} size="small" fullWidth sx={fieldSx} InputLabelProps={{ shrink: true }} />
              </FieldLabel>

              <FieldLabel label={t18n("ARRIVAL_VALIDITY_RULES.allowed_to")} required={form.is_closed !== 1} error={errors.allowed_to}>
                <TextField type="time" value={form.allowed_to} disabled={form.is_closed === 1}
                  onChange={(e) => setForm({ ...form, allowed_to: e.target.value })} size="small" fullWidth sx={fieldSx} InputLabelProps={{ shrink: true }} />
              </FieldLabel>

              <Box sx={{ gridColumn: { xs: "auto", sm: "1 / span 2" } }}>
                <FieldLabel label={t18n("ARRIVAL_VALIDITY_RULES.invalid_note")}>
                  <TextField value={form.invalid_note} onChange={(e) => setForm({ ...form, invalid_note: e.target.value })} size="small" fullWidth multiline minRows={2} sx={fieldSx} />
                </FieldLabel>
              </Box>

              <Box sx={{ gridColumn: { xs: "auto", sm: "1 / span 2" }, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: t.surfaceMuted, borderRadius: 2, px: 2, py: 1 }}>
                <Typography sx={{ fontFamily: "Cairo-Medium", fontSize: 14, color: t.text }}>
                  {form.enabled === 1 ? t18n("ARRIVAL_VALIDITY_RULES.enabled") : t18n("ARRIVAL_VALIDITY_RULES.disabled")}
                </Typography>
                <Switch checked={form.enabled === 1} onChange={(e) => setForm({ ...form, enabled: e.target.checked ? 1 : 0 })} />
              </Box>
            </Box>
          </Box>

          {/* footer: primary first (visual-right in RTL) */}
          <Box sx={{ display: "flex", gap: 1.5, p: 2.5, borderTop: `1px solid ${t.border}` }}>
            <Button onClick={submit} disabled={saving} variant="contained" disableElevation
              startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon />}
              sx={{ bgcolor: t.primary, "&:hover": { bgcolor: t.primaryDark }, fontFamily: "Cairo-Bold", borderRadius: 2, px: 3 }}>
              {t18n("GLOBAL.Save")}
            </Button>
            <Button onClick={() => setForm(null)} disabled={saving} variant="outlined"
              sx={{ color: t.text, borderColor: t.border, "&:hover": { borderColor: t.borderStrong, bgcolor: t.surfaceMuted }, fontFamily: "Cairo-Bold", borderRadius: 2, px: 3 }}>
              {t18n("GLOBAL.cancel")}
            </Button>
          </Box>
        </ModalShell>
      )}

      {/* ---------------- Recompute modal ---------------- */}
      {showRecompute && (
        <RecomputeModal
          t={t} tt={t18n} destinations={destinations}
          onClose={() => setShowRecompute(false)}
          fieldSx={fieldSx}
        />
      )}

      {/* ---------------- Delete confirm ---------------- */}
      {confirmRow && (
        <ModalShell onClose={() => !deleting && setConfirmRow(null)} busy={deleting} title={t18n("GLOBAL.are_you_sure")} icon={WarningAmberIcon} accent={t.danger} accentSoft={t.dangerSoft} maxWidth={440}>
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontFamily: "Cairo", color: t.textMuted, fontSize: 14 }}>
              {t18n("ARRIVAL_VALIDITY_RULES.delete_confirm")}
            </Typography>
            {confirmRow?.name ? (
              <Typography sx={{ mt: 1, fontFamily: "Cairo-Bold", color: t.text, fontSize: 15 }}>{confirmRow.name}</Typography>
            ) : null}
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, p: 2.5, borderTop: `1px solid ${t.border}` }}>
            <Button onClick={doDelete} disabled={deleting} variant="contained" disableElevation
              startIcon={deleting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <DeleteOutlineIcon />}
              sx={{ bgcolor: t.danger, "&:hover": { bgcolor: "#DC2626" }, fontFamily: "Cairo-Bold", borderRadius: 2, px: 3 }}>
              {t18n("GLOBAL.action_delete")}
            </Button>
            <Button onClick={() => setConfirmRow(null)} disabled={deleting} variant="outlined"
              sx={{ color: t.text, borderColor: t.border, "&:hover": { borderColor: t.borderStrong, bgcolor: t.surfaceMuted }, fontFamily: "Cairo-Bold", borderRadius: 2, px: 3 }}>
              {t18n("GLOBAL.cancel")}
            </Button>
          </Box>
        </ModalShell>
      )}
    </Box>
  );
}

/* =============================================================== *
 *  Inline helpers (kept in one feature file per §F.13)            *
 * =============================================================== */

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: t.control, borderRadius: 2, fontFamily: "Cairo", fontSize: 14,
    "& fieldset": { borderColor: t.border },
    "&:hover fieldset": { borderColor: t.borderStrong },
    "&.Mui-focused fieldset": { borderColor: t.primary, borderWidth: 2 },
  },
  "& .MuiInputBase-input": { color: t.text },
  "& .MuiSelect-select": { fontFamily: "Cairo", fontSize: 14 },
};

const filterFieldSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": { ...fieldSx["& .MuiOutlinedInput-root"], bgcolor: t.surfaceMuted },
};

// Cairo font for the dropdown option list (rendered in a portal, so it needs
// its own styling — fieldSx only reaches the closed select value).
const selectMenuProps = {
  PaperProps: { sx: { "& .MuiMenuItem-root": { fontFamily: "Cairo", fontSize: 14 } } },
};
const selectProps = { MenuProps: selectMenuProps };

const toggleGroupSx = {
  mt: 0.5,
  "& .MuiToggleButton-root": {
    fontFamily: "Cairo-Medium", fontSize: 13, textTransform: "none", color: t.textMuted,
    borderColor: t.border, px: 2, py: 0.5,
    "&.Mui-selected": { bgcolor: t.primary, color: t.onPrimary, "&:hover": { bgcolor: t.primaryDark } },
  },
};

const menuItemSx = (tk) => ({
  display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, cursor: "pointer",
  fontSize: 14, color: tk.text, transition: "background .15s",
  "&:hover": { bgcolor: tk.surfaceMuted },
});

function FieldLabel({ label, required, error, children }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.text, fontFamily: "Cairo-Medium" }}>
        {label}{required && <Box component="span" sx={{ color: t.danger, mr: 0.5 }}>*</Box>}
      </Typography>
      {children}
      {error && <Typography sx={{ fontSize: 12, color: t.danger, fontFamily: "Cairo" }}>{error}</Typography>}
    </Box>
  );
}

function FilterCaption({ children }) {
  return <Typography sx={{ fontSize: 12, fontWeight: 700, color: t.textMuted, fontFamily: "Cairo-Medium", textTransform: "uppercase", letterSpacing: "0.04em" }}>{children}</Typography>;
}

/* 24-hour timeline bar: green = allowed arrival window, red = rejected. LTR so
 * 00:00 is on the left and 24:00 on the right (like the reference schedule). */
function TimelineBar({ rule, tt }) {
  const closed = parseInt(rule.is_closed) === 1;
  const toMin = (s) => { if (!s) return null; const p = String(s).split(":"); return (parseInt(p[0]) || 0) * 60 + (parseInt(p[1]) || 0); };
  const hhmm = (s) => (s ? String(s).substring(0, 5) : "");
  const pct = (m) => `${(m / 1440) * 100}%`;
  const fromM = toMin(rule.allowed_from);
  const toM = toMin(rule.allowed_to);
  const hasWindow = fromM != null && toM != null;

  const segs = [];
  if (!closed) {
    if (hasWindow) {
      if (fromM <= toM) segs.push([fromM, toM]);
      else { segs.push([fromM, 1440]); segs.push([0, toM]); } // overnight window
    } else {
      segs.push([0, 1440]); // no time restriction
    }
  }
  const acceptedText = hasWindow
    ? `${tt("ARRIVAL_VALIDITY_RULES.accepted")} (${hhmm(rule.allowed_from)} - ${hhmm(rule.allowed_to)})`
    : tt("ARRIVAL_VALIDITY_RULES.accepted_allday");

  const ticks = [{ m: 0, label: "00:00", sub: tt("ARRIVAL_VALIDITY_RULES.midnight") }];
  if (!closed && hasWindow) {
    ticks.push({ m: fromM, label: hhmm(rule.allowed_from) });
    ticks.push({ m: toM, label: hhmm(rule.allowed_to) });
  }
  ticks.push({ m: 1440, label: "24:00", sub: tt("ARRIVAL_VALIDITY_RULES.midnight") });

  return (
    <Box sx={{ mt: 3 }}>
      {/* tick labels */}
      <Box dir="ltr" sx={{ position: "relative", height: 30 }}>
        {ticks.map((tk, i) => {
          const atStart = tk.m === 0, atEnd = tk.m === 1440;
          return (
            <Box key={i} sx={{ position: "absolute", left: pct(tk.m), transform: atStart ? "none" : atEnd ? "translateX(-100%)" : "translateX(-50%)" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: t.text, fontFamily: "Cairo-Medium", lineHeight: 1.1, whiteSpace: "nowrap", textAlign: "center" }}>{tk.label}</Typography>
              {tk.sub ? <Typography sx={{ fontSize: 10, color: t.textMuted, fontFamily: "Cairo", whiteSpace: "nowrap", textAlign: "center" }}>{tk.sub}</Typography> : null}
            </Box>
          );
        })}
      </Box>

      {/* bar */}
      <Box dir="ltr" sx={{ position: "relative", height: 46, borderRadius: 1.5, overflow: "hidden", bgcolor: t.danger, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {closed ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, zIndex: 1 }}>
            <BlockIcon sx={{ color: "#fff", fontSize: 20 }} />
            <Typography sx={{ color: "#fff", fontFamily: "Cairo-Bold", fontSize: 14, textAlign: "center" }}>{tt("ARRIVAL_VALIDITY_RULES.closed_full")}</Typography>
          </Box>
        ) : segs.map((s, i) => (
          <Box key={i} sx={{ position: "absolute", top: 0, bottom: 0, left: pct(s[0]), width: pct(s[1] - s[0]), bgcolor: t.success }} />
        ))}
      </Box>

      {/* legend */}
      {!closed ? (
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75, flexWrap: "wrap", gap: 0.5 }}>
          <Typography sx={{ fontSize: 12, color: t.success, fontFamily: "Cairo-Medium" }}>● {acceptedText}</Typography>
          <Typography sx={{ fontSize: 12, color: t.danger, fontFamily: "Cairo-Medium" }}>● {tt("ARRIVAL_VALIDITY_RULES.rejected_outside")}</Typography>
        </Box>
      ) : null}
    </Box>
  );
}

/* Hand-rolled modal overlay (§F.3) */
function ModalShell({ onClose, busy, title, icon: Icon, accent, accentSoft, maxWidth = 560, children }) {
  const headAccent = accent || t.primary;
  const headSoft = accentSoft || t.primarySoft;
  return (
    <Box
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      sx={{ position: "fixed", inset: 0, zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(15,23,42,0.55)", p: 2 }}
    >
      <Slide direction="down" in timeout={220}>
        <Box sx={{ bgcolor: t.surface, borderRadius: 3, border: `1px solid ${t.border}`, boxShadow: "0 20px 55px rgba(0,0,0,0.28)", width: "100%", maxWidth, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2.5, borderBottom: `1px solid ${t.border}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: headSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {Icon && <Icon sx={{ color: headAccent, fontSize: 22 }} />}
              </Box>
              <Typography sx={{ fontFamily: "Cairo-Bold", fontSize: 17, color: t.text }}>{title}</Typography>
            </Box>
            <IconButton onClick={() => !busy && onClose()} sx={{ color: t.textMuted, "&:hover": { bgcolor: t.surfaceMuted, color: t.text } }}>
              <CloseIcon />
            </IconButton>
          </Box>
          {children}
        </Box>
      </Slide>
    </Box>
  );
}

/* Recompute / backfill modal */
function RecomputeModal({ t: tk, tt, destinations, onClose, fieldSx: fSx }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [destination, setDestination] = useState("");
  const [limit, setLimit] = useState(1000);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState(null);

  const run = async () => {
    setRunning(true); setSummary(null);
    const res = await arrivalValidityRuleAPI.recomputeArrivalValidity({
      data: {
        arrived_at_from: from ? from.replace("T", " ").substring(0, 16) + ":00" : "",
        arrived_at_to: to ? to.replace("T", " ").substring(0, 16) + ":00" : "",
        lifecycle_type: 1, // external-only (خارجي)
        destination_geofence_id: destination || null,
        limit: parseInt(limit) || 1000,
      },
    });
    setRunning(false);
    if (res?.status && res?.data?.status) {
      setSummary(res?.data?.data?.summary || res?.data?.data);
      toast.success(tt("ARRIVAL_VALIDITY_RULES.recompute_done"));
    } else {
      toast.error(getMsg(res, tt("NETWORK_MESSAGES.unknownError")));
    }
  };

  const stat = (label, value, color) => (
    <Box sx={{ flex: "1 1 30%", minWidth: 120, bgcolor: tk.surfaceMuted, borderRadius: 2, p: 1.25, textAlign: "center" }}>
      <Typography sx={{ fontFamily: "Cairo-Bold", fontSize: 20, color: color || tk.text }}>{value ?? 0}</Typography>
      <Typography sx={{ fontFamily: "Cairo", fontSize: 12, color: tk.textMuted }}>{label}</Typography>
    </Box>
  );

  return (
    <ModalShell onClose={onClose} busy={running} title={tt("ARRIVAL_VALIDITY_RULES.recompute_title")} icon={ReplayIcon} accent={tk.warning} accentSoft={tk.warningSoft} maxWidth={560}>
      <Box sx={{ p: 3, overflowY: "auto", maxHeight: "62vh" }}>
        <Typography sx={{ fontFamily: "Cairo", fontSize: 13, color: tk.textMuted, mb: 2 }}>
          {tt("ARRIVAL_VALIDITY_RULES.recompute_hint")}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <FieldLabel label={tt("ARRIVAL_VALIDITY_RULES.arrived_from")}>
            <TextField type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} size="small" fullWidth sx={fSx} InputLabelProps={{ shrink: true }} />
          </FieldLabel>
          <FieldLabel label={tt("ARRIVAL_VALIDITY_RULES.arrived_to")}>
            <TextField type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} size="small" fullWidth sx={fSx} InputLabelProps={{ shrink: true }} />
          </FieldLabel>
          <FieldLabel label={tt("ARRIVAL_VALIDITY_RULES.destination")}>
            <TextField select value={destination} onChange={(e) => setDestination(e.target.value)} size="small" fullWidth sx={fSx} SelectProps={selectProps}>
              <MenuItem value="">{tt("ARRIVAL_VALIDITY_RULES.any_destination")}</MenuItem>
              {destinations.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
          </FieldLabel>
          <FieldLabel label={tt("ARRIVAL_VALIDITY_RULES.limit")}>
            <TextField type="text" inputMode="numeric" value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^\d]/g, ""))} size="small" fullWidth sx={fSx} inputProps={{ dir: "ltr", style: { textAlign: "left" } }} />
          </FieldLabel>
        </Box>

        {summary && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mt: 2.5 }}>
            {stat(tt("ARRIVAL_VALIDITY_RULES.sum_candidates"), summary.total_candidates ?? summary.evaluated)}
            {stat(tt("ARRIVAL_VALIDITY_RULES.sum_marked_invalid"), summary.marked_invalid, tk.danger)}
            {stat(tt("ARRIVAL_VALIDITY_RULES.sum_valid"), summary.valid, tk.success)}
            {stat(tt("ARRIVAL_VALIDITY_RULES.sum_skipped"), summary.skipped)}
            {stat(tt("ARRIVAL_VALIDITY_RULES.sum_errors"), summary.errors, tk.warning)}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, p: 2.5, borderTop: `1px solid ${tk.border}` }}>
        <Button onClick={run} disabled={running} variant="contained" disableElevation
          startIcon={running ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ReplayIcon />}
          sx={{ bgcolor: tk.warning, "&:hover": { bgcolor: "#D97706" }, fontFamily: "Cairo-Bold", borderRadius: 2, px: 3 }}>
          {running ? tt("ARRIVAL_VALIDITY_RULES.running") : tt("ARRIVAL_VALIDITY_RULES.run_recompute")}
        </Button>
        <Button onClick={onClose} disabled={running} variant="outlined"
          sx={{ color: tk.text, borderColor: tk.border, "&:hover": { borderColor: tk.borderStrong, bgcolor: tk.surfaceMuted }, fontFamily: "Cairo-Bold", borderRadius: 2, px: 3 }}>
          {tt("GLOBAL.cancel")}
        </Button>
      </Box>
    </ModalShell>
  );
}
